import { RadioGroup as MuiRadioGroup, FormLabel, FormControl } from '@mui/material';
import type {
  FormControlProps,
  FormLabelProps,
  RadioGroupProps as MuiRadioGroupProps,
} from '@mui/material';
import { Radio, RadioProps } from './Radio';

export type RadioGroupProps = MuiRadioGroupProps & {
  id?: string;
  label?: string;
  name: string;
  formLabelProps?: Omit<FormLabelProps, 'children'>;
  formControlProps?: FormControlProps;
  radios?: RadioProps[];
};

/**
 * RadioGroup component
 * @param id - optional id for the form label element
 * @param label - optional label to display above the radio group
 * @param name - name attribute for the radio group, required for proper form behavior
 * @param formLabelProps - additional props to pass to the FormLabel component
 * @param formControlProps - additional props to pass to the FormControl component
 * @param radios - array of radio button configurations to render
 * @param children - Radio components to render inside the group
 * @param onChange - callback function to be called when the selected radio value changes
 * @param defaultValue - the default selected value
 * @param value - the controlled selected value
 * @param row - whether to display radio buttons in a row (horizontal) instead of column (vertical)
 */
export const RadioGroup = ({
  children,
  label,
  id,
  onChange,
  formLabelProps,
  formControlProps,
  radios,
  ...props
}: RadioGroupProps) => {
  return (
    <FormControl {...formControlProps}>
      {label && (
        <FormLabel {...formLabelProps} id={id}>
          {label}
        </FormLabel>
      )}
      <MuiRadioGroup {...props} onChange={onChange}>
        {radios?.map(radio => <Radio key={radio?.id} {...radio} />)}
        {children}
      </MuiRadioGroup>
    </FormControl>
  );
};
